import React, { useEffect, useRef } from 'react';
import { actions, containers, elements, feedback } from '@ecosystem/newron-design-system';
import AISkeletonLoader from './AISkeletonLoader';
import { AiFeedback } from './feedback/AiFeedback';

const { IconButton } = actions;
const { Card } = containers;
const { Badge } = feedback;

interface AISignalsCardProps {
  endpoint?: string;
  requestId?: string;
  insightSummary: { heading: string; text?: string }[] | Record<string, any>;
  loading?: boolean;
  onClose?: () => void;
}

const CardTitle = ({ loading, onClose }) => {
  return (
    <Card.Title>
      {loading ? (
        <span className="text-gradient-ai">Generating...</span>
      ) : (
        <div className="flex items-start">
          <b className="font-bold">AI Signals</b>
          <div className="pl-2">
            <Badge state={'premium'}>Beta</Badge>
          </div>
        </div>
      )}
      {onClose && (
        <Card.Title.Actions>
          <IconButton onClick={onClose}>
            <elements.Icon.icons.Close />
          </IconButton>
        </Card.Title.Actions>
      )}
    </Card.Title>
  );
};

const CardContent = ({ insightSummary, loading }) => {
  return (
    <Card.Content>
      {loading && <AISkeletonLoader loading={loading} />}
      {insightSummary && (
        <div className="mb-4">
          {Array.isArray(insightSummary) && (
            <ul className="pl-5" style={{ listStyleType: 'disc' }}>
              {insightSummary.map(item => (
                <li key={item.heading} className="mb-2">
                  <span className="font-bold">{item.heading}:</span>
                  {item.text ? <> {item.text}</> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card.Content>
  );
};

const CardFooter = ({ endpoint, requestId }) => {
  return (
    <Card.Footer align="justify" gradient={true}>
      <span className="text-sm">
        {' '}
        AI is used to generate responses based on proprietary NIQ data so mistakes are possible. Please review output
        carefully before use.
      </span>
      {endpoint && requestId && <AiFeedback endpoint={endpoint} requestId={requestId} />}
    </Card.Footer>
  );
};

const AISignalsCard: React.FC<AISignalsCardProps> = ({ endpoint, requestId, insightSummary, loading, onClose }) => {
  const childRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cardContainer = childRef.current?.closest('.component-content');
    if (!cardContainer) return;
    if (loading) {
      cardContainer.classList.remove('overflow-x-clip');
    } else {
      cardContainer.classList.add('overflow-x-clip');
    }
  }, [loading]);

  if (!loading && !insightSummary) return null;

  return (
    <div ref={childRef}>
      <Card gradient={true} isgradientLoading={loading}>
        <CardTitle loading={loading} onClose={onClose} />
        <CardContent loading={loading} insightSummary={insightSummary} />
        <CardFooter endpoint={endpoint} requestId={requestId} />
      </Card>
    </div>
  );
};

export default AISignalsCard;
